import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getApiClient } from '@apps/web/lib/api_client';
import { type components } from '@libraries/typescript/api_client';

type PhoebeAgentRunEventRead = components['schemas']['PhoebeAgentRunEventRead'];
type AdminAgentRunEventHistoryPage =
    components['schemas']['AdminAgentRunEventHistoryPage'];

const HISTORY_PAGE_SIZE = 100;

type HistoryState = {
    readonly runId: string | null;
    readonly eventsById: ReadonlyMap<string, PhoebeAgentRunEventRead>;
    readonly nextBeforeOffset: number | null | undefined;
    readonly forwardAfterOffset: number;
    readonly isReady: boolean;
    readonly isLoadingInitial: boolean;
    readonly isLoadingOlder: boolean;
    readonly error: Error | null;
};

const INITIAL_HISTORY_STATE: HistoryState = {
    runId: null,
    eventsById: new Map(),
    nextBeforeOffset: undefined,
    forwardAfterOffset: -1,
    isReady: false,
    isLoadingInitial: false,
    isLoadingOlder: false,
    error: null,
};

export type AdminAgentRunEventHistory = {
    readonly eventsNewestFirst: Array<PhoebeAgentRunEventRead>;
    readonly forwardAfterOffset: number;
    readonly hasOlder: boolean;
    readonly isReady: boolean;
    readonly isLoadingInitial: boolean;
    readonly isLoadingOlder: boolean;
    readonly error: Error | null;
    readonly loadOlder: () => void;
    readonly retry: () => void;
};

export function mergeAdminAgentRunEvents(
    ...eventGroups: ReadonlyArray<ReadonlyArray<PhoebeAgentRunEventRead>>
): Array<PhoebeAgentRunEventRead> {
    const eventsById = new Map<string, PhoebeAgentRunEventRead>();
    const eventIdByOffset = new Map<number, string>();
    for (const events of eventGroups) {
        for (const event of events) {
            const existingIdAtOffset = eventIdByOffset.get(event.event_offset);
            if (existingIdAtOffset && existingIdAtOffset !== event.id) {
                throw new Error(
                    `Conflicting event ids for offset ${event.event_offset}`,
                );
            }
            const previous = eventsById.get(event.id);
            if (
                previous !== undefined &&
                previous.event_offset !== event.event_offset
            ) {
                throw new Error(`Conflicting offsets for event ${event.id}`);
            }
            if (previous) {
                eventIdByOffset.delete(previous.event_offset);
            }
            eventsById.set(event.id, event);
            eventIdByOffset.set(event.event_offset, event.id);
        }
    }
    return [...eventsById.values()].sort(
        (left, right) => left.event_offset - right.event_offset,
    );
}

function mergePage(
    state: HistoryState,
    page: AdminAgentRunEventHistoryPage,
): Pick<
    HistoryState,
    'eventsById' | 'nextBeforeOffset' | 'forwardAfterOffset'
> {
    const events = mergeAdminAgentRunEvents(
        [...state.eventsById.values()],
        page.events,
    );
    return {
        eventsById: new Map(events.map((event) => [event.id, event])),
        nextBeforeOffset: page.next_before_offset,
        forwardAfterOffset: state.isReady
            ? state.forwardAfterOffset
            : page.forward_after_offset,
    };
}

function toError(error: unknown): Error {
    return error instanceof Error
        ? error
        : new Error('Couldn’t load event history');
}

export function useAdminAgentRunEventHistory({
    runId,
    enabled,
}: {
    readonly runId: string;
    readonly enabled: boolean;
}): AdminAgentRunEventHistory {
    const [state, setState] = useState<HistoryState>(INITIAL_HISTORY_STATE);
    const generationRef = useRef(0);
    const activeRunIdRef = useRef(runId);
    const olderRequestInFlightRef = useRef(false);
    const nextBeforeOffsetRef = useRef<number | null | undefined>(undefined);
    const stateMatchesRun = state.runId === runId;
    activeRunIdRef.current = runId;

    const loadPage = useCallback(
        async (beforeOffset: number | null, generation: number) => {
            const page = await getApiClient().get({
                url: '/admin/agent/runs/{run_id}/event-history',
                pathParams: { run_id: runId },
                queryParams: {
                    before_offset: beforeOffset ?? undefined,
                    limit: HISTORY_PAGE_SIZE,
                },
                reason: 'admin-agent-run-event-history',
            });
            return generation === generationRef.current &&
                runId === activeRunIdRef.current
                ? page
                : null;
        },
        [runId],
    );

    const loadInitial = useCallback(() => {
        generationRef.current += 1;
        const generation = generationRef.current;
        olderRequestInFlightRef.current = false;
        nextBeforeOffsetRef.current = undefined;
        setState({
            ...INITIAL_HISTORY_STATE,
            runId,
            isLoadingInitial: enabled,
        });
        if (!enabled) {
            return;
        }
        void loadPage(null, generation)
            .then((page) => {
                if (page === null) {
                    return;
                }
                nextBeforeOffsetRef.current = page.next_before_offset;
                setState((current) => ({
                    ...current,
                    ...mergePage(current, page),
                    isReady: true,
                    isLoadingInitial: false,
                }));
            })
            .catch((error: unknown) => {
                if (
                    generation !== generationRef.current ||
                    runId !== activeRunIdRef.current
                ) {
                    return;
                }
                setState((current) => ({
                    ...current,
                    isLoadingInitial: false,
                    error: toError(error),
                }));
            });
    }, [enabled, loadPage, runId]);

    useEffect(() => {
        loadInitial();
        return () => {
            generationRef.current += 1;
            olderRequestInFlightRef.current = false;
        };
    }, [loadInitial]);

    const loadOlder = useCallback(() => {
        const beforeOffset = nextBeforeOffsetRef.current;
        if (
            !enabled ||
            beforeOffset === null ||
            beforeOffset === undefined ||
            !stateMatchesRun ||
            olderRequestInFlightRef.current
        ) {
            return;
        }
        olderRequestInFlightRef.current = true;
        const generation = generationRef.current;
        setState((current) => ({
            ...current,
            isLoadingOlder: true,
            error: null,
        }));
        void loadPage(beforeOffset, generation)
            .then((page) => {
                if (page === null) {
                    return;
                }
                nextBeforeOffsetRef.current = page.next_before_offset;
                setState((current) => ({
                    ...current,
                    ...mergePage(current, page),
                    isLoadingOlder: false,
                }));
            })
            .catch((error: unknown) => {
                if (
                    generation !== generationRef.current ||
                    runId !== activeRunIdRef.current
                ) {
                    return;
                }
                setState((current) => ({
                    ...current,
                    isLoadingOlder: false,
                    error: toError(error),
                }));
            })
            .finally(() => {
                if (
                    generation === generationRef.current &&
                    runId === activeRunIdRef.current
                ) {
                    olderRequestInFlightRef.current = false;
                }
            });
    }, [enabled, loadPage, runId, stateMatchesRun]);

    const retry = useCallback(() => {
        if (stateMatchesRun && state.isReady) {
            loadOlder();
            return;
        }
        loadInitial();
    }, [loadInitial, loadOlder, state.isReady, stateMatchesRun]);

    const eventsNewestFirst = useMemo(
        () =>
            (stateMatchesRun ? [...state.eventsById.values()] : []).sort(
                (left, right) => right.event_offset - left.event_offset,
            ),
        [state.eventsById, stateMatchesRun],
    );

    return {
        eventsNewestFirst,
        forwardAfterOffset: stateMatchesRun ? state.forwardAfterOffset : -1,
        hasOlder:
            stateMatchesRun && state.nextBeforeOffset !== null && state.isReady,
        isReady: stateMatchesRun && state.isReady,
        isLoadingInitial: stateMatchesRun && state.isLoadingInitial,
        isLoadingOlder: stateMatchesRun && state.isLoadingOlder,
        error: stateMatchesRun ? state.error : null,
        loadOlder,
        retry,
    };
}
